import React from 'react';
import { Device } from '../../lib/types';
import { formatTimestamp } from '../../lib/utils';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import BatteryIndicator from './BatteryIndicator';
import styles from './Devices.module.scss';

interface DeviceRowProps {
  device: Device;
  onAddPet: (device: Device) => void;
  onTransferFoster: (device: Device) => void;
  onRecall: (device: Device) => void;
  onTransferAdopter: (device: Device) => void;
  onSendSms: (device: Device) => void;
}

export default function DeviceRow({
  device,
  onAddPet,
  onTransferFoster,
  onRecall,
  onTransferAdopter,
  onSendSms,
}: DeviceRowProps) {
  const isOnline = device.status === 'online';

  // Setup pills
  const szVariant = device.safeZone ? 'success' : 'error';
  const wifiVariant = device.wifiConfigured ? 'success' : 'error';

  // Actions depend on holder type and dog assignment
  let actions: React.ReactNode;
  if (device.holderType === 'rescue' && !device.petName) {
    actions = (
      <Button variant="green" size="sm" onClick={() => onAddPet(device)}>
        Add Pet
      </Button>
    );
  } else if (device.holderType === 'foster') {
    actions = (
      <div className={styles.rowActions}>
        <Button variant="ghost" size="sm" onClick={() => onSendSms(device)}>
          Send SMS
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onRecall(device)}>
          Recall
        </Button>
      </div>
    );
  } else {
    actions = (
      <div className={styles.rowActions}>
        <Button variant="green" size="sm" onClick={() => onTransferAdopter(device)}>
          Adopt
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onTransferFoster(device)}>
          Foster
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onRecall(device)}>
          Remove Pet
        </Button>
      </div>
    );
  }

  return (
    <tr>
      <td>
        <span className={styles.holderName}>{device.holderName}</span>
      </td>
      <td>{device.petName || <span className={styles.textMuted}>Unassigned</span>}</td>
      <td>
        <div className={styles.deviceId}>{device.id}</div>
        <div className={styles.deviceType}>{device.type}</div>
      </td>
      <td>
        <div className={styles.setupPills}>
          <StatusBadge variant={szVariant}>Safe Zone</StatusBadge>
          <StatusBadge variant={wifiVariant}>WiFi</StatusBadge>
        </div>
      </td>
      <td>
        {isOnline ? (
          <>
            <div className={styles.statusCell}>
              <span className={`${styles.statusDot} ${styles.statusOnline}`} />
              Online
            </div>
            <div className={styles.lastSeen}>Now</div>
          </>
        ) : (
          <a
            href="https://support.tryfi.com/hc/en-us/articles/360033210214-Offline-Fi-Device-Troubleshooting"
            target="_blank"
            rel="noopener noreferrer"
            title="Troubleshooting: Offline Fi Device"
            className={styles.offlineLink}
          >
            <div className={styles.statusCell}>
              <span className={`${styles.statusDot} ${styles.statusOffline}`} />
              Offline
            </div>
            <div className={styles.lastSeen}>{formatTimestamp(device.lastSeen)}</div>
          </a>
        )}
      </td>
      <td>
        <BatteryIndicator battery={device.battery} />
      </td>
      <td>{actions}</td>
    </tr>
  );
}
